/**
 * MagicLink.jsx — Email link landing page with PWA-aware handoff.
 *
 * Flow:
 *   1. Email opens this page (often in Safari on iOS, not the installed PWA).
 *   2. We verify the token and store the session locally.
 *   3. We broadcast `auth:login` so any already-open PWA window picks it up.
 *   4. We show a calm, premium success screen with TWO calls to action:
 *        • Öppna Trainnova   — primary, stays on the current app origin
 *        • Fortsätt här     — secondary, stays in the current browser tab
 *   5. iOS cannot reliably auto-open an installed PWA from Safari; we set
 *      that expectation explicitly with a calm one-line instruction.
 *
 * Sync applies to same-origin windows that share browser storage. An iOS
 * standalone app may have separate storage; this is not a session transfer.
 *   • localStorage write (token/user) triggers `storage` event in other tabs
 *     — AuthContext listens for this and re-hydrates without a page reload.
 *   • BroadcastChannel('trainova-auth') fires `{type:'login', user}` so any
 *     open SPA instance can react instantly.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { verifyMagicLink } from "../lib/api";
import { googleRequest } from "../lib/directGoogle";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import LoginLanguageSwitcher from "../components/auth/LoginLanguageSwitcher";
import { authFlowMessage, authErrorMessage, isTemporaryAuthError } from "../lib/authFlow";

// `display-mode: standalone` is the cross-browser signal that we are already
// inside an installed PWA (iOS Safari + Chromium + Firefox all support it).
function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  // iOS legacy flag
  if (window.navigator.standalone === true) return true;
  // Modern browsers
  try {
    return window.matchMedia?.("(display-mode: standalone)").matches === true;
  } catch {
    return false;
  }
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function broadcastLogin(user) {
  // BroadcastChannel — instant push to any open tabs/windows of the SPA.
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const ch = new BroadcastChannel("trainova-auth");
      ch.postMessage({ type: "login", user, ts: Date.now() });
      // Close on next tick so other listeners receive the message first.
      setTimeout(() => ch.close(), 100);
    }
  } catch {
    /* no-op — environments without BroadcastChannel still work via storage */
  }
}

export default function MagicLink() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();
  return <MagicLinkFlow key={token} token={token} />;
}

function MagicLinkFlow({ token }) {
  const { t, i18n } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const loginRef = useRef(loginUser);
  loginRef.current = loginUser;
  const translationRef = useRef({ t, i18n });
  translationRef.current = { t, i18n };
  const [status, setStatus] = useState("verifying");
  // 'verifying' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  const [retryable, setRetryable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(null);
  const standalone = isStandalonePWA();
  const ios = isIOS();

  useEffect(() => {
    let cancelled = false;
    setStatus("verifying");
    setErrorMsg("");
    setRetryable(false);
    if (!token) {
      setStatus("error");
      setErrorMsg(translationRef.current.t("auth:magic_verify.error_missing_token"));
      return;
    }

    // Share the in-flight verification between StrictMode effect runs. A
    // persisted "verified" flag is never proof of a still-valid session.
    if (!requestRef.current || requestRef.current.attempt !== attempt) {
      requestRef.current = { attempt, promise: verifyMagicLink(token) };
    }

    (async () => {
      let verified = false;
      try {
        const res = await requestRef.current.promise;
        if (cancelled) return;
        verified = true;
        await loginRef.current(res.data.access_token, res.data.user);
        if (cancelled) return;
        if (localStorage.getItem("token") !== res.data.access_token) {
          setStatus("error");
          setErrorMsg(authFlowMessage(translationRef.current.i18n, "sessionChanged"));
          return;
        }
        broadcastLogin(res.data.user);

        // Same-browser email tabs share the HttpOnly pending-flow cookie.
        let pendingGoogle = null;
        try { pendingGoogle = await googleRequest('/google/flow', undefined, 'GET'); } catch { /* no Google flow */ }
        if (cancelled) return;
        if (pendingGoogle?.status === 'verified') {
          navigate('/auth/continue', { replace: true });
          return;
        }

        if (standalone) {
          navigate("/", { replace: true });
          return;
        }

        setStatus("success");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setRetryable(!verified && isTemporaryAuthError(err));
        setErrorMsg(verified
          ? authFlowMessage(translationRef.current.i18n, "sessionChanged")
          : authErrorMessage(err, translationRef.current.i18n, "magic"));
      }
    })();
    return () => { cancelled = true; };
  }, [token, attempt, standalone, navigate]);

  const handleOpenApp = () => {
    // Preview and production have separate sessions. Stay on the origin
    // that verified the link; opening an installed PWA depends on the browser.
    try {
      window.location.assign("/");
    } catch {
      navigate("/", { replace: true });
    }
  };

  const handleStayHere = () => {
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex items-start justify-center py-10 px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[rgb(var(--tn-text-ffffff))] tracking-tight">
            {t("common:brand.name")}
          </h1>
        </div>

        <Card className="bg-card border-border" data-testid="magic-link-verify-card">
          <CardContent className="pt-8 pb-8 text-center space-y-5">
            {status === "verifying" && (
              <>
                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
                <h2
                  data-testid="magic-link-verifying-title"
                  className="font-heading text-2xl text-[rgb(var(--tn-text-ffffff))]"
                >
                  {t("auth:magic_verify.verifying_title")}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {t("auth:magic_verify.verifying_body")}
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <h2
                  data-testid="magic-link-success-title"
                  className="font-heading text-2xl text-[rgb(var(--tn-text-ffffff))]"
                >
                  {t("auth:magic_verify.success_title")}
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("auth:magic_verify.success_body")}
                </p>

                <div className="pt-2 space-y-2">
                  <Button
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={handleOpenApp}
                    data-testid="magic-link-open-app-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t("auth:magic_verify.open_app")}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleStayHere}
                    data-testid="magic-link-continue-here-btn"
                  >
                    {t("auth:magic_verify.stay_here")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {ios && (
                  <p
                    data-testid="magic-link-ios-hint"
                    className="text-[11px] text-muted-foreground/80 pt-3 leading-relaxed"
                  >
                    {t("auth:magic_verify.ios_hint")}
                  </p>
                )}
              </>
            )}

            {status === "error" && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-destructive/15 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl text-[rgb(var(--tn-text-ffffff))]">
                  {t("auth:magic_verify.error_title")}
                </h2>
                <p role="alert" className="text-muted-foreground text-sm leading-relaxed" data-testid="magic-link-error">
                  {errorMsg}
                </p>
                <div className="pt-2 space-y-3">
                  {retryable && <Button type="button" variant="outline" className="w-full" onClick={() => setAttempt(value => value + 1)} data-testid="magic-link-retry-btn">
                    {authFlowMessage(i18n, "retry")}
                  </Button>}
                  <Link to="/login?mode=magic" className="block">
                    <Button
                      className="w-full bg-primary text-primary-foreground"
                      data-testid="magic-link-back-btn"
                    >
                      {t("auth:magic_verify.request_new_link")}
                    </Button>
                  </Link>
                  <Link to="/login?mode=password" className="block">
                    <Button variant="ghost" className="w-full">{authFlowMessage(i18n, "passwordLogin")}</Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <LoginLanguageSwitcher />
      </div>
    </div>
  );
}
